import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./ReviewsList.module.css";
import RatingStars from "./RatingStars";
import { ReviewItem } from "../../../types/requestsOutputs";

interface ReviewsListProps {
    reviews: ReviewItem[];
    title: string;
    countLabel: string;
    emptyLabel: string;
    noCommentLabel: string;
}

// Date formatter pinned to fr-FR to match the rest of the analytics screen.
const DATE_FMT = new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
});

const formatDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return DATE_FMT.format(d);
};

const ReviewsList: React.FC<ReviewsListProps> = ({ reviews, title, countLabel, emptyLabel, noCommentLabel }) => {
    const { t } = useTranslation();
    const tt = t as unknown as (k: string) => string;

    return (
        <section className={styles.card} aria-label={title}>
            <header className={styles.header}>
                <h3 className={styles.title}>{title}</h3>
                <p className={styles.count}>{countLabel}</p>
            </header>

            {reviews.length === 0 ? (
                <p className={styles.empty}>{emptyLabel}</p>
            ) : (
                <div className={styles.scroll}>
                    {reviews.map((r) => (
                        <article key={r.id} className={styles.row}>
                            <div className={styles.topLine}>
                                <RatingStars value={r.score} ariaLabel={`${r.score} ${tt("reviewsStarsOutOf")} 5`} />
                                <div className={styles.meta}>
                                    <span className={styles.trackTag}>{r.trackName}</span>
                                    <span className={styles.metaSep} aria-hidden />
                                    <span>{formatDate(r.createdAt)}</span>
                                </div>
                            </div>
                            {r.content ? (
                                <p className={styles.content}>{r.content}</p>
                            ) : (
                                <p className={styles.noComment}>{noCommentLabel}</p>
                            )}
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
};

export default ReviewsList;
